import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Home, Eye, EyeOff, Loader2 } from "lucide-react";
import { z } from "zod";
import { explainPasswordError } from "@/lib/password-errors";

// Validation schema - matches Auth.tsx for consistency
const passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(128, "Password too long");

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Set once a reset succeeds, so the session check below doesn't report the
  // (expected) post-sign-out "no session" state as an invalid link.
  const resetCompleteRef = useRef(false);
  const [linkState, setLinkState] = useState<"checking" | "valid" | "invalid">("checking");

  useEffect(() => {
    let cancelled = false;

    // The recovery session can still be settling from the URL on first load,
    // and minor clock skew ("issued in the future") can briefly delay it, so
    // poll with a grace period before deciding. We do NOT auto-navigate here:
    // bouncing to /auth on a transient miss previously fought with the global
    // recovery redirect and produced a navigation loop. Instead we show an
    // inline state and let the user act.
    const verifyLink = async () => {
      for (let attempt = 0; attempt < 8; attempt++) {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled || resetCompleteRef.current) return;
        if (session) {
          setLinkState("valid");
          return;
        }
        await new Promise((r) => setTimeout(r, 400));
      }
      if (cancelled || resetCompleteRef.current) return;
      setLinkState("invalid");
    };

    verifyLink();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate password
      passwordSchema.parse(password);

      if (password !== confirmPassword) {
        toast({
          title: "Passwords Don't Match",
          description: "Please make sure both passwords are the same.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: password,
        // Clear the admin-set "must change password" flag (no-op for normal resets).
        data: { must_change_password: false },
      });

      if (error) {
        // Say what actually went wrong. This used to report every failure as
        // "Unable to reset password. Please try again or request a new reset
        // link.", which is wrong for the commonest case by far: a family who
        // cannot get in retypes the password they believe they set, and the
        // server rejects it for being the one already on the account. Told to
        // "try again", they try the same password again. (21 Sep 2026.)
        const raw = error.message ?? "";
        const same = /different from the old password/i.test(raw);
        const noSession = /session|token/i.test(raw) && /missing|expired|invalid/i.test(raw);
        toast({
          title: same ? "That is already your password" : "Reset Failed",
          description: same
            ? "Choose a password you have not used on this account before."
            : noSession
              ? "This reset link has expired. Request a new one from the sign-in page and open it within the hour."
              : explainPasswordError(raw) ?? (raw || "Unable to reset password. Please try again or request a new reset link."),
          variant: "destructive",
        });
        // The server's own words, for whoever is asked to look into it.
        console.error("[reset-password] updateUser failed:", raw);
        setLoading(false);
      } else {
        resetCompleteRef.current = true;
        toast({
          title: "Password Reset Successful",
          description: "Your password has been updated. You can now log in with your new password.",
        });
        setPassword("");
        setConfirmPassword("");
        // Sign out and redirect to login
        await supabase.auth.signOut();
        navigate("/auth");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        const raw = error instanceof Error ? error.message : String(error);
        toast({
          title: "Error",
          description: raw || "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
        console.error("[reset-password] unexpected:", raw);
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20 p-4">
      <div className="absolute top-4 left-4">
        <Button onClick={() => navigate("/")} variant="outline">
          <Home className="mr-2 h-4 w-4" />
          Back to Website
        </Button>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          <CardDescription>
            {linkState === "invalid"
              ? "This reset link is invalid or has expired."
              : "Enter your new password below"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {linkState === "invalid" ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                Please request a new password reset link from the login page.
              </p>
              <Button className="w-full" onClick={() => navigate("/auth")}>
                Back to Login
              </Button>
            </div>
          ) : linkState === "checking" ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Verifying your reset link…</p>
            </div>
          ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimum 8 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate("/auth")}
            >
              Back to Login
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
